<template>
  <div class="commercialBreakWrapper" :class="{ visible: canShowCommercialBreak }">
    <div class="commercialBreakHeader">
      <div class="timer">Reklama zostanie<br />zamknięta za {{ redirectTime }} sek.</div>
      <button @click.prevent="close">Idź do serwisu</button>
    </div>
    <div class="commercialBreak" v-if="isContentTypeImage">
      <a
        v-if="commercialBreake.targetUrl"
        :href="commercialBreake.targetUrl"
        aria-label="Przejście na stronę reklamy"
        target="_blank"
      >
        <img
          class="commercialBreakeImg"
          :src="commercialBreake.imgSrc"
          :title="commercialBreake.title"
          :alt="commercialBreake.alt"
        />
      </a>
      <div v-if="!commercialBreake.targetUrl">
        <img
          class="commercialBreakeImg"
          :src="commercialBreake.imgSrc"
          :title="commercialBreake.title"
          :alt="commercialBreake.alt"
        />
      </div>
    </div>
    <div class="commercialBreak" v-if="isContentTypeVideo" v-html="commercialBreake.videoContainer"></div>
    <div class="commercialBreak htmlContent" v-if="isContentTypeHtml">
      <iframe
        :src="commercialBreake.htmlUrl"
        :width="commercialBreake.width"
        :height="commercialBreake.height"
        class="ad-banner"
      ></iframe>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { COMMERCIAL_BREAK_REDIRECT_TIME } from '@/base/const/confirmation.const'
import { ToolsClass } from '@/base/class/tools.class'
import { ADS_BANNER_SIZE, ADS_CONTENT_TYPE } from '@/base/const/ads.const'

export default {
  name: 'CommercialBreak',
  data() {
    return {
      redirectHandler: null,
      redirectTime: COMMERCIAL_BREAK_REDIRECT_TIME,
      confirmed: null,
    }
  },
  created() {
    if (this.$VUE_COMMERCIAL_BREAK_REDIRECT_TIME) {
      this.redirectTime = this.$VUE_COMMERCIAL_BREAK_REDIRECT_TIME
    }
  },
  computed: {
    ...mapState({
      transaction: state => state.confirmation.data?.transaction,
      showCommercialBreake: (state) => state.ads.showCommercialBreake,
    }),
    ...mapGetters({
      commercialBreake: 'getCommercialBreak',
    }),
    canShowCommercialBreak: function () {
      return !!this.commercialBreake && this.showCommercialBreake
    },
    isContentTypeImage: function () {
      return this.commercialBreake && this.commercialBreake.contentType === ADS_CONTENT_TYPE.image
    },
    isContentTypeVideo: function () {
      return this.commercialBreake && this.commercialBreake.contentType === ADS_CONTENT_TYPE.video
    },
    isContentTypeHtml: function () {
      return this.commercialBreake && this.commercialBreake.contentType === ADS_CONTENT_TYPE.html
    },
  },
  watch: {
    commercialBreake: function (val, oldVal) {
      if (val && val !== oldVal && this.isContentTypeVideo) {
        ToolsClass.parseAndLoadExternalScriptToHeader(this.commercialBreake.scriptEmbed, () => {
          setTimeout(() => {
            const rootDiv = document.querySelector('div.commercialBreak')
            if (rootDiv) {
              this.setHeightRecursive(rootDiv)
            }
          }, 1000)
        })
      }
    },
    canShowCommercialBreak: function (val) {
      if (val && !this.redirectHandler) {
        if (this.isContentTypeImage || this.isContentTypeVideo || this.isContentTypeHtml) {
          this.startRedirectTimeout()
          this.sendViewConfirmation()
        }
      }
    },
  },
  methods: {
    ...mapMutations(['SET_ADS_COMMERCIAL_BREAK_CLOSED', 'SET_ADS_SIZE']),
    ...mapActions(['sendAdsBannerViewConfirmation']),
    sendViewConfirmation() {
      if (!!this.confirmed && this.confirmed === this.commercialBreake.creationId) {
        return
      }
      const data = {
        creationId: this.commercialBreake.creationId,
        transRemoteID: this.transaction?.transRemoteID
      }
      this.sendAdsBannerViewConfirmation(data)
        .catch(() => {})
        .finally(() => {
          this.confirmed = this.commercialBreake.creationId
        })
    },
    setWindowSize() {
      if (ToolsClass.isMobileScreenSize()) {
        this.SET_ADS_SIZE(ADS_BANNER_SIZE.mobile)
        return
      }
      this.SET_ADS_SIZE(ADS_BANNER_SIZE.desktop)
    },
    close() {
      this.stopRedirectTimeout()
      this.SET_ADS_COMMERCIAL_BREAK_CLOSED(true)
    },
    stopRedirectTimeout() {
      if (this.redirectHandler) {
        clearTimeout(this.redirectHandler)
      }
    },
    startRedirectTimeout() {
      this.redirectHandler = setTimeout(this.checkRedirectTimeout, 1000)
    },
    checkRedirectTimeout() {
      if (this.redirectTime === 1) {
        this.close()
        return
      }
      this.redirectTime--
      this.startRedirectTimeout()
    },
    setHeightRecursive(element) {
      // Jeśli nie ma elementu, nic nie rób
      if (!element) return
      // Znajdź div bezpośrednio wewnątrz aktualnego elementu
      const child = element.querySelector(':scope > div, :scope > iframe')
      if (child) {
        // Ustaw block-size: 100% dla znalezionego div
        child.classList.add('fullheight')

        // Rekurencyjnie wykonaj to samo dla tego dziecka
        // Rekurencja tylko jeśli child jest div
        if (child.tagName.toLowerCase() === 'div') {
          this.setHeightRecursive(child)
        }
      }
    },
  },
  mounted() {
    window.addEventListener('resize', () => {
      this.setWindowSize()
    })
    window.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') {
        this.close()
      }
    })
  },
}
</script>

<style lang="scss">
@use '../../assets/scss/variables' as *;
@use '../../assets/scss/mixins' as *;

.commercialBreakWrapper {
  position: absolute;
  inline-size: 100%;
  block-size: 100%;
  inset-block-start: 0;
  left: 0;
  z-index: 100;
  background: #000;
  display: none;
  flex-direction: column;
  align-items: center;

  &.visible {
    display: flex;
  }

  .fullheight {
    block-size: 100% !important;
  }

  .commercialBreakHeader {
    background: #fff;
    inline-size: 100%;
    block-size: 70px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    gap: 20px;

    @include sm {
      justify-content: flex-end;
    }

    .timer {
      inline-size: 120px;
      font-size: 11px;
      line-height: 14px;
      color: #000;
    }

    button {
      background-color: var(--button-bg-default);
      block-size: 30px;
      border-radius: 15px;
      border: none;
      padding: 5px 20px;
      font-size: 11px;
      color: #fff;
      cursor: pointer;
    }
  }

  .commercialBreak {
    inline-size: 100%;
    block-size: calc(100% - 70px);
    padding: 10px;
    display: block;
    text-align: center;

    @include sm {
      inline-size: 80%;
      padding: 40px;
    }

    &.fullscreen {
      a {
        inline-size: 100%;
        block-size: 100%;
      }

      img {
        inline-size: 100%;
        block-size: 100%;
        object-fit: cover;
      }
    }

    &.htmlContent {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  iframe {
    border: 0;
    overflow: hidden;
    margin: 0 auto;
    display: block;
  }
}
</style>
